import { useCallback, useMemo, useRef, useState } from 'react';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import { notify } from 'src/utils/notifications';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import { getIsIosSafari } from 'src/utils/browser';
import { saveAs } from 'file-saver';
import useCloseBottomDrawer from 'src/components/Drawers/BaseDrawers/BottomDrawer/hooks/useCloseBottomDrawer';
import useModal from 'src/components/Modals/hooks/useModal';
import {
	PnlModalProps,
	PnlModalCardPreviewVariantProps,
} from 'src/components/Modals/PnlModal/types';

export default function usePnlModal(params: PnlModalProps) {
	const { hide } = useModal();
	const currentAccountKey = useDriftAccountStore((s) => s.currentUserKey);
	const setAccountStore = useDriftAccountStore((s) => s.set);
	const isMobileScreenSize = useIsMobileScreenSize();
	const [backgroundImagePathIndex, setBackgroundImagePathIndex] = useState(0);
	const [showCollateral, setShowCollateral] = useSyncLocalStorage(
		'pnlModalShowCollateral',
		true
	);
	const [isDownloading, setIsDownloading] = useState(false);
	const [isCopying, setIsCopying] = useState(false);
	const [isCopied, setIsCopied] = useState(false);

	const imageGenerationNodeRef = useRef<HTMLDivElement>(null);

	const { marketSymbol, marketIndex } =
		'position' in params ? params.position : {};

	const handleDownload = useCallback(async () => {
		if (!imageGenerationNodeRef.current || isDownloading || isCopying) {
			return;
		}

		setIsDownloading(true);

		try {
			const blob = await generateImageBlob(
				imageGenerationNodeRef.current,
				isMobileScreenSize
			);

			const fileName = `drift-pnl-${
				marketSymbol ? `${marketSymbol}` : 'portfolio'
			}.png`;

			// If we're on iOS, the standard download method doesn't seem to work
			// well with Phantom. So we first try to use the Web Share API.
			if (getIsIosSafari()) {
				if (!navigator.share) {
					// If `navigator.share` is not supported (iOS < 2019), we fall back
					// to showing a toast that allows the user to copy the image.
					throw new Error('navigator.share is not supported');
				}

				const file = new File([blob], fileName, {
					type: 'image/png',
				});

				return navigator.share({ files: [file] });
			}

			// Otherwise, we just try a standard download.
			saveAs(blob, fileName);
		} catch (error) {
			console.log('[usePnlModal] error downloading image', error);

			notify({
				type: 'error',
				message: 'Error downloading image. Try copying it instead.',
			});
		} finally {
			setIsDownloading(false);
		}
	}, [isDownloading, isCopying, marketSymbol, isMobileScreenSize]);

	const handleCopy = useCallback(async () => {
		if (!imageGenerationNodeRef.current || isDownloading || isCopying) {
			return;
		}

		setIsCopying(true);

		try {
			await navigator.clipboard.write([
				// This has to be done in this very strange way because, as usual, Safari.
				// See here for more details: https://web.dev/async-clipboard/.
				new ClipboardItem({
					'image/png': new Promise((res) => {
						if (!imageGenerationNodeRef.current) {
							return;
						}

						generateImageBlob(
							imageGenerationNodeRef.current,
							isMobileScreenSize
						).then((blob) => {
							res(blob);
						});
					}),
				}),
			]);

			setIsCopied(true);
		} catch (error) {
			console.log('[usePnlModal] error copying image', error);

			notify({ type: 'error', message: 'Error copying image.' });
		} finally {
			setIsCopying(false);
		}
	}, [isDownloading, isCopying, isMobileScreenSize]);

	const handleClose = useCallback(() => {
		setAccountStore((state) => {
			if (
				!currentAccountKey ||
				!state.accounts[currentAccountKey] ||
				marketIndex === undefined
			) {
				return;
			}

			// Clear the pnlInfo for this specific market index
			const userAccountData = state.accounts[currentAccountKey];
			if (userAccountData.pnlInfo[marketIndex]) {
				delete userAccountData.pnlInfo[marketIndex];
			}

			return state;
		});

		hide();
	}, [currentAccountKey, setAccountStore, hide, marketIndex]);

	const {
		closeDrawer: handleCloseMobileDrawer,
		isDrawerClosing: isMobileDrawerClosing,
	} = useCloseBottomDrawer({ onClose: handleClose });

	const cardPreviewVariantProps = useMemo(() => {
		if (params.triggeredFrom === 'portfolio-performance') {
			return {
				variant: 'portfolio-performance',
				portfolioData: {
					triggeredFrom: params.triggeredFrom,
					accountName: params.accountName,
					pnl: params.pnl,
					period: params.period,
					startOfPeriodTs: params.startOfPeriodTs,
				},
				positionData: undefined,
			} satisfies PnlModalCardPreviewVariantProps;
		}

		return {
			variant: 'position',
			positionData: {
				position: params.position,
				actualExitPrice: params.actualExitPrice,
				pnl: params.pnl,
				triggeredFrom: params.triggeredFrom,
				isIsolated: params.isIsolated,
			},
			portfolioData: undefined,
		} satisfies PnlModalCardPreviewVariantProps;
	}, [params]);

	return {
		backgroundImagePathIndex,
		setBackgroundImagePathIndex,
		showCollateral,
		setShowCollateral,
		isMobileScreenSize,
		imageGenerationNodeRef,
		handleCloseMobileDrawer,
		isMobileDrawerClosing,
		handleDownload,
		handleCopy,
		handleClose,
		isDownloading,
		isCopying,
		isCopied,
		cardPreviewVariantProps,
	};
}

async function generateImageBlob(
	imageGenerationNode: HTMLDivElement,
	isMobileScreenSize: boolean
) {
	const { domToBlob } = await import('modern-screenshot');
	const scale = isMobileScreenSize ? 3 : 2;

	return domToBlob(imageGenerationNode, {
		quality: 1,
		scale,
	});
}
